import React, { useState, useCallback, useEffect, lazy, Suspense } from 'react';
import LeftPanel from './components/LeftPanel';
import CerfaForm from './components/CerfaForm';
import AdminLabelingPage from './components/AdminLabelingPage';
import AdminValuationPage from './components/AdminValuationPage';
import LandingPage from './components/LandingPage';
import DossiersPage from './components/DossiersPage';
import { CerfaState, INITIAL_STATE, AppStep, GarageProfile, UserSession } from './types';
import { extractCarteGriseData, extractIdentityData } from './services/geminiService';
import { promoteTrainingSample } from './services/trainingService';
import { fetchCurrentUser, logout, startGoogleLoginWithPrompt } from './services/authService';
import { fetchGarageProfile, saveGarageProfileRemote } from './services/profileService';
import { createDossierFromCerfa } from './services/autoDossierService';
import { getDossier, updateDossierCerfaState, uploadDossierDocument, deleteDossierDocument } from './services/dossierService';
import { startCheckout } from './services/billingService';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const App: React.FC = () => {
  const [formData, setFormData] = useState<CerfaState>(INITIAL_STATE);
  const [currentStep, setCurrentStep] = useState<AppStep>(AppStep.UPLOAD_CARTE_GRISE);
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [garageProfile, setGarageProfile] = useState<GarageProfile | null>(null);
  const [currentTaskId, setCurrentTaskId] = useState<string | null>(null);
  const [previewScale, setPreviewScale] = useState(1);
  const [isValidated, setIsValidated] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [currentUser, setCurrentUser] = useState<UserSession | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [page, setPage] = useState<'landing' | 'cerfa' | 'labeling' | 'dossiers' | 'valuation'>('landing');
  const [lastIdentityTaskId, setLastIdentityTaskId] = useState<string | null>(null);
  const [pendingDossierId, setPendingDossierId] = useState<number | null>(null);
  const [activeDossierId, setActiveDossierId] = useState<number | null>(null);
  const [cerfaDirty, setCerfaDirty] = useState(false);
  const [cgTaskId, setCgTaskId] = useState<string | null>(null);
  const isAdminEmail = currentUser?.email?.toLowerCase() === 'sas.t.livemedia@gmail.com';
  const isAdmin = !!(currentUser?.is_admin || isAdminEmail);
  const isPaying = !!(isAdmin || currentUser?.is_paying);

  const setPageWithUrl = (nextPage: 'landing' | 'cerfa' | 'labeling' | 'dossiers' | 'valuation') => {
    setPage(nextPage);
    if (typeof window !== "undefined") {
      try {
        localStorage.setItem('lastPage', nextPage);
      } catch (e) {
        console.warn("Unable to persist lastPage", e);
      }
      const params = new URLSearchParams(window.location.search);
      if (nextPage === 'dossiers') params.set('page', 'dossiers');
      else if (nextPage === 'cerfa') params.set('page', 'cerfa');
      else if (nextPage === 'landing') params.set('page', 'landing');
      else if (nextPage === 'labeling') params.set('page', 'labeling');
      else if (nextPage === 'valuation') params.set('page', 'valuation');
      // automarket désactivé
      else params.delete('page');
      const qs = params.toString();
      const newUrl = `${window.location.pathname}${qs ? `?${qs}` : ''}`;
      window.history.replaceState({}, '', newUrl);
    }
  };
  const isAdminLabeling = typeof window !== "undefined" && window.location.pathname.startsWith("/admin/labeling");

  // Load garage profile on startup
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const pageParam = params.get('page');
    const dossierParam = params.get('dossierId');
    if (dossierParam) {
      const parsed = parseInt(dossierParam, 10);
          if (!Number.isNaN(parsed)) setPendingDossierId(parsed);
        }
    let targetPage: 'landing' | 'cerfa' | 'labeling' | 'dossiers' | 'valuation' = 'cerfa';
    if (pageParam === 'dossiers') targetPage = 'dossiers';
    else if (pageParam === 'cerfa') targetPage = 'cerfa';
    else if (pageParam === 'landing') targetPage = 'landing';
    else if (pageParam === 'labeling') targetPage = 'labeling';
    else if (pageParam === 'valuation') targetPage = 'valuation';
    // automarket désactivé
    else {
      try {
        const saved = localStorage.getItem('lastPage') as any;
        if (saved === 'dossiers' || saved === 'cerfa' || saved === 'landing' || saved === 'labeling') {
          targetPage = saved;
        }
      } catch (e) {
        // ignore
      }
    }

    fetchCurrentUser()
      .then((user) => {
        setCurrentUser(user);
        if (user) {
          if (targetPage === 'labeling' && !user.is_admin) {
            setPage('cerfa');
          } else {
            setPage(targetPage);
          }
        } else {
          setPage('landing');
        }
      })
      .finally(() => setAuthLoading(false));

    // Chargement profil garage (localStorage puis backend si connecté)
    const savedProfile = localStorage.getItem('garageProfile');
    if (savedProfile) {
      try {
        const parsed = JSON.parse(savedProfile);
        setGarageProfile(parsed);
        setFormData(prev => ({
          ...prev,
          seller: {
            ...prev.seller,
            role: 'seller',
            isCompany: true,
            lastName: parsed.name,
            companyName: parsed.name,
            siret: parsed.siret,
            address: parsed.address,
            addressLine1: parsed.address,
            country: parsed.country ?? prev.seller.country,
            postalCode: parsed.postalCode,
            city: parsed.city
          }
        }));
      } catch (e) { console.error("Error parsing saved profile"); }
    }

    // Responsive scaling listener
    const handleResize = () => {
      const width = window.innerWidth;
      if (width < 640) { // Mobile
        const availableWidth = width - 32;
        setPreviewScale(availableWidth / 800); 
      } else if (width < 1024) { // Tablet
        const availableWidth = width - 40;
        setPreviewScale(availableWidth / 820);
      } else { // Desktop
        const panelWidth = width * 0.55;
        setPreviewScale(Math.min(1, panelWidth / 840));
      }
    };
    
    window.addEventListener('resize', handleResize);
    handleResize(); 
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Sync garage profile depuis backend quand user connecté
  useEffect(() => {
    if (!currentUser) return;
    const syncProfile = async () => {
      const remote = await fetchGarageProfile();
      if (remote) {
        saveGarageProfile(remote);
      }
    };
    void syncProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.email]);

  // Pré-remplir le Cerfa depuis un dossier si dossierId dans l'URL
  useEffect(() => {
    if (!currentUser || !pendingDossierId) return;
    const load = async () => {
      try {
        const dossier = await getDossier(pendingDossierId);
        setActiveDossierId(pendingDossierId);
        if ((dossier as any).cerfa_state) {
          setFormData((dossier as any).cerfa_state as CerfaState);
        } else {
          setFormData(prev => ({
            ...prev,
            vehicle: {
              ...prev.vehicle,
              registrationNumber: dossier.immatriculation || prev.vehicle.registrationNumber,
              vin: dossier.vin || prev.vehicle.vin,
              brand: prev.vehicle.brand,
              model: prev.vehicle.model,
            },
          }));
        }
        setPage('cerfa');
      } catch (e) {
        console.error("Impossible de charger le dossier pour préremplissage", e);
      } finally {
        setPendingDossierId(null);
      }
    };
    void load();
  }, [currentUser, pendingDossierId]);

  // Invalidate validation if data changes
  const handleDataChange = (newData: CerfaState) => {
    setFormData(newData);
    if (isValidated) setIsValidated(false);
    setCerfaDirty(true);
    // Si on édite un Cerfa déjà scanné depuis un dossier, on bascule direct sur l'étape 3
    if (activeDossierId && currentStep !== AppStep.REVIEW) {
      setCurrentStep(AppStep.REVIEW);
    }
  };

  const saveGarageProfile = (profile: GarageProfile) => {
    setGarageProfile(profile);
    localStorage.setItem('garageProfile', JSON.stringify(profile));
    setFormData(prev => ({
        ...prev,
        seller: {
            ...prev.seller,
            role: 'seller',
            isCompany: true,
            lastName: profile.name,
            companyName: profile.name,
            siret: profile.siret,
            address: profile.address,
            addressLine1: profile.address,
            country: profile.country ?? prev.seller.country,
            postalCode: profile.postalCode,
            city: profile.city
        }
    }));
    // Sauvegarde côté backend si connecté
    void saveGarageProfileRemote(profile);
  };

  const handleLogout = async () => {
    await logout();
    setCurrentUser(null);
    setPage('landing');
  };

  const handleSwitchAccount = async () => {
    await logout();
    startGoogleLoginWithPrompt();
  };

  const handleUpload = useCallback(async (file: File, type: 'carteGrise' | 'identity', role: 'buyer' | 'seller' = 'buyer') => {
    setIsProcessing(true);
    setErrorMsg(null);
    setIsValidated(false);
    try {
      if (type === 'carteGrise') {
        const extracted = await extractCarteGriseData(file);
        setFormData(prev => ({
          ...prev,
          vehicle: { ...prev.vehicle, ...extracted.vehicle },
        }));
        if ((extracted as any).taskId) {
          const tid = (extracted as any).taskId as string;
          setCurrentTaskId(tid);
          setCgTaskId(tid);
        }
        // On repart sur une nouvelle CG : on évite de réutiliser un ancien task_id d'identité.
        setLastIdentityTaskId(null);
        setCurrentStep(AppStep.UPLOAD_ID);
      } else if (type === 'identity') {
        const isSeller = role === 'seller';
        const extracted = await extractIdentityData(file, isSeller);
        setFormData(prev => ({
          ...prev,
          [isSeller ? 'seller' : 'buyer']: {
            ...(isSeller ? prev.seller : prev.buyer),
            ...(isSeller ? extracted.seller : extracted.buyer)
          }
        }));
        if (extracted.taskId) {
          setLastIdentityTaskId(extracted.taskId);
        }
        setCurrentStep(AppStep.REVIEW);
      }
    } catch (error) {
      console.error(error);
      setErrorMsg("Une erreur est survenue lors de l'analyse. Veuillez réessayer.");
    } finally {
      setIsProcessing(false);
    }
  }, []);

  const handleNext = () => {
    if (currentStep === AppStep.UPLOAD_CARTE_GRISE) {
        setCurrentStep(AppStep.UPLOAD_ID);
    } else if (currentStep === AppStep.UPLOAD_ID) {
        setCurrentStep(AppStep.REVIEW);
    }
  };

  const handleBack = () => {
    if (currentStep === AppStep.UPLOAD_ID) {
        setCurrentStep(AppStep.UPLOAD_CARTE_GRISE);
    } else if (currentStep === AppStep.REVIEW) {
        setCurrentStep(AppStep.UPLOAD_ID);
    }
  };

  const handleValidate = async () => {
    setIsValidated(true);
    setSuccessMsg(null);
    const taskIdForTraining = cgTaskId || currentTaskId;
    if (!taskIdForTraining) {
      setErrorMsg("Aucun taskId : uploade et OCR d'abord pour générer la tâche (carte grise).");
      return;
    }
    try {
      await promoteTrainingSample({
        task_id: taskIdForTraining,
        fields: {
          registration_number: formData.vehicle.registrationNumber,
          vin: formData.vehicle.vin,
          first_registration_date: formData.vehicle.firstRegistrationDate,
          brand: formData.vehicle.brand,
          type: formData.vehicle.type,
          model: formData.vehicle.model,
        }
      });
      // Création automatique du dossier avec les documents disponibles
      try {
        const res = await createDossierFromCerfa({
          immatriculation: formData.vehicle.registrationNumber,
          vin: formData.vehicle.vin,
          brand: formData.vehicle.brand,
          model: formData.vehicle.model,
          task_id_carte_grise: cgTaskId || taskIdForTraining,
          task_id_identity: lastIdentityTaskId || undefined,
          titre: `${formData.vehicle.brand} ${formData.vehicle.model} ${formData.vehicle.registrationNumber}`.trim(),
          cerfa_state: formData,
        });
        // Attache le PDF Cerfa au dossier
        try {
          setIsGeneratingPdf(true);
          const { blob, fileName } = await generateCerfaPdfBlob();
          const file = new File([blob], fileName, { type: "application/pdf" });
          await uploadDossierDocument(res.id, file, "cerfa_pdf");
          setActiveDossierId(res.id);
        } catch (err) {
          console.warn("Impossible d'attacher le PDF Cerfa au dossier", err);
        } finally {
          setIsGeneratingPdf(false);
        }
      } catch (e) {
        console.warn("Création auto du dossier échouée", e);
      }
      setSuccessMsg("Échantillon envoyé à l'entraînement et dossier créé.");
      // On réinitialise les task_id pour éviter de réinjecter des anciens fichiers dans le prochain dossier.
      setCgTaskId(null);
      setCurrentTaskId(null);
      setLastIdentityTaskId(null);
    } catch (e) {
      console.error("Promotion training failed", e);
      setErrorMsg("Échec d'envoi à l'entraînement");
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const generateCerfaPdfBlob = async (): Promise<{ blob: Blob; fileName: string }> => {
    await new Promise(resolve => setTimeout(resolve, 800));
    const element = document.getElementById('cerfa-pdf-source');
    if (!element) throw new Error("Form element not found");

    const canvas = await html2canvas(element, { 
        scale: 2, 
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        windowWidth: 1200, 
        windowHeight: 1600,
        width: 794,
        height: 1123,
        scrollX: 0,
        scrollY: 0,
        x: 0,
        y: 0
    });
    
    const imgData = canvas.toDataURL('image/jpeg', 0.9);
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pdfWidth = 210;
    const pdfHeight = 297;
    
    pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight);
    pdf.addPage();
    pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight);
    
    const fileName = `cerfa_cession_${formData.vehicle.registrationNumber || 'auto'}_${new Date().toISOString().split("T")[0]}.pdf`;
    const blob = pdf.output("blob");
    return { blob, fileName };
  };

  const handleDownload = async () => {
    if (!isValidated) return;
    setIsProcessing(true);
    setIsGeneratingPdf(true);
    try {
        const { blob, fileName } = await generateCerfaPdfBlob();
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = fileName;
        link.click();
        URL.revokeObjectURL(link.href);
    } catch (e) {
        console.error("PDF Gen Error", e);
        alert("Erreur lors de la génération. Une impression classique va être lancée.");
        window.print(); 
    } finally {
        setIsGeneratingPdf(false);
        setIsProcessing(false);
    }
  };

  const handleSaveToDossier = async () => {
    if (!activeDossierId) return;
    setIsProcessing(true);
    try {
      await updateDossierCerfaState(activeDossierId, {
        cerfa_state: formData,
        immatriculation: formData.vehicle.registrationNumber?.replace(/\s+/g, ""),
        vin: formData.vehicle.vin,
        titre: `${formData.vehicle.brand || ""} ${formData.vehicle.model || ""} ${formData.vehicle.registrationNumber || ""}`.trim(),
      });
      // Supprime les anciens PDF Cerfa
      const detail = await getDossier(activeDossierId);
      const cerfaDocs = (detail as any).documents?.filter((d: any) => d.type === "cerfa_pdf") || [];
      for (const doc of cerfaDocs) {
        await deleteDossierDocument(activeDossierId, doc.id);
      }
      // Génère et pousse le nouveau PDF
      setIsGeneratingPdf(true);
      const { blob, fileName } = await generateCerfaPdfBlob();
      const file = new File([blob], fileName, { type: "application/pdf" });
      await uploadDossierDocument(activeDossierId, file, "cerfa_pdf");
      setSuccessMsg("Cerfa mis à jour dans le dossier.");
      setCerfaDirty(false);
    } catch (e) {
      console.error("Save to dossier error", e);
      setErrorMsg("Impossible de sauvegarder le Cerfa dans le dossier.");
    } finally {
      setIsGeneratingPdf(false);
      setIsProcessing(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 text-white">
        Chargement...
      </div>
    );
  }

  if (!currentUser) {
    return <LandingPage />;
  }

  if (isAdminLabeling && page === 'labeling') {
    if (!currentUser.is_admin) {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-slate-900 text-white">
          <p className="text-lg font-semibold">Accès admin requis.</p>
          <button
            className="mt-4 px-4 py-2 bg-white text-slate-900 rounded"
            onClick={handleLogout}
          >
            Se déconnecter
          </button>
        </div>
      );
    }
    // Affiche l'admin avec la barre haute (boutons Scanner / Déconnexion)
    return (
      <div className="flex flex-col h-screen w-screen overflow-hidden bg-slate-900 font-inter text-slate-100">
        <div className="flex items-center justify-between px-4 py-2 bg-slate-800 text-sm">
          <div className="text-slate-300">
            Connecté en tant que <span className="font-semibold text-white">{currentUser.email}</span>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => (window.location.href = "/")}
              className="px-3 py-1 rounded bg-slate-200 text-slate-900 font-semibold hover:bg-slate-100 transition"
            >
              Scanner
            </button>
            <button
              onClick={handleSwitchAccount}
              className="px-3 py-1 rounded bg-slate-200 text-slate-900 font-semibold hover:bg-slate-100 transition"
            >
              Changer de compte
            </button>
            <button
              onClick={handleLogout}
              className="px-3 py-1 rounded bg-white text-slate-900 font-semibold hover:bg-slate-100 transition"
            >
              Se déconnecter
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-hidden">
          <AdminLabelingPage />
        </div>
      </div>
    );
  }

  const TopBar = (
    <div className="flex items-center justify-between px-4 py-2 bg-slate-800 text-sm">
      <div className="text-slate-300">
        Connecté en tant que <span className="font-semibold text-white">{currentUser.email}</span>
      </div>
     <div className="flex items-center space-x-2 flex-wrap">
        {!isPaying && (
          <button
            onClick={() => startCheckout()}
            className="px-3 py-1 rounded bg-amber-400 text-slate-900 font-semibold hover:bg-amber-300 transition"
            title="Activer l'abonnement Pro (19,90€/mois)"
          >
            Activer l’abonnement Pro
          </button>
        )}
        {isAdmin && (
          <button
            onClick={() => setPageWithUrl('labeling')}
            className="px-3 py-1 rounded bg-slate-200 text-slate-900 font-semibold hover:bg-slate-100 transition"
          >
            Annotation
          </button>
        )}
        {isAdmin && (
          <button
            onClick={() => setPageWithUrl('valuation')}
            className="px-3 py-1 rounded bg-slate-200 text-slate-900 font-semibold hover:bg-slate-100 transition"
          >
            Cotes marché
          </button>
        )}
        {/* AutoMarket AI désactivé */}
        <button
          onClick={() => isPaying && setPageWithUrl('dossiers')}
          className={`px-3 py-1 rounded font-semibold transition ${isPaying ? "bg-slate-200 text-slate-900 hover:bg-slate-100" : "bg-slate-600 text-slate-300 cursor-not-allowed"}`}
          title={isPaying ? "" : "Fonctionnalité Pro"}
        >
          Mes dossiers
        </button>
        <button
          onClick={() => setPageWithUrl('cerfa')}
          className="px-3 py-1 rounded bg-slate-200 text-slate-900 font-semibold hover:bg-slate-100 transition"
        >
          Scanner
        </button>
        <button
          onClick={handleSwitchAccount}
          className="px-3 py-1 rounded bg-slate-200 text-slate-900 font-semibold hover:bg-slate-100 transition"
        >
          Changer de compte
        </button>
        <button
          onClick={handleLogout}
          className="px-3 py-1 rounded bg-white text-slate-900 font-semibold hover:bg-slate-100 transition"
        >
          Se déconnecter
        </button>
      </div>
    </div>
  );

  // Pages rendues selon l'état
  if (page === 'labeling' && isAdmin) {
    return (
      <div className="flex flex-col h-screen w-screen overflow-hidden bg-slate-900 font-inter text-slate-100">
        {TopBar}
        <div className="flex-1 overflow-hidden">
          <AdminLabelingPage />
        </div>
      </div>
    );
  }

  if (page === 'valuation' && isAdmin) {
    return (
      <div className="flex flex-col h-screen w-screen overflow-hidden bg-slate-900 font-inter text-slate-100">
        {TopBar}
        <div className="flex-1 overflow-auto">
          <AdminValuationPage />
        </div>
      </div>
    );
  }

  // Page automarket désactivée

  if (page === 'dossiers') {
    return (
      <div className="flex flex-col h-screen w-screen overflow-hidden bg-slate-900 font-inter text-slate-100">
        {TopBar}
        <div className="flex-1 overflow-auto">
          <DossiersPage />
        </div>
      </div>
    );
  }

  if (page === 'cerfa') {
    return (
      <div className="flex flex-col h-screen w-screen overflow-hidden bg-slate-900 font-inter text-slate-100">
        {TopBar}

    <div className="flex flex-1 flex-col md:flex-row overflow-hidden">

      {/* Bouton d'annotation retiré : l'annotation est accessible via une page admin dédiée */}

      {/* 
        CAPTURE CONTAINER
        Positioned fixed but outside the viewport (top-left 0, z-index -100) 
        and using visibility: hidden to allow html2canvas to capture it.
        display: none would NOT work.
        Using a dedicated container wrapper to enforce width context.
      */}
      {isGeneratingPdf && (
          <div style={{ 
              position: 'fixed', 
              top: 0, 
              left: 0, 
              width: '794px', // Force A4 width context
              height: '1123px',
              zIndex: -100, // Behind everything
              overflow: 'hidden',
              background: 'white' // Ensure background is white for capture
          }}>
             <CerfaForm data={formData} isPdfMode={true} />
          </div>
      )}

      {/* Loading Overlay when generating PDF */}
      {isGeneratingPdf && (
         <div className="fixed inset-0 z-[1000] bg-slate-900/90 flex flex-col items-center justify-center text-white backdrop-blur-sm">
            <span className="material-symbols-outlined text-4xl animate-spin mb-4">settings</span>
            <p className="text-xl font-bold">Génération du PDF...</p>
            <p className="text-sm text-slate-400">Veuillez patienter.</p>
         </div>
      )}

      {/* Notifications */}
      {errorMsg && (
        <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-50 bg-red-600 text-white px-6 py-3 rounded-lg shadow-xl flex items-center animate-bounce border border-red-400">
          <span className="material-symbols-outlined mr-2">warning</span>
          {errorMsg}
          <button onClick={() => setErrorMsg(null)} className="ml-4 hover:text-red-200">
             <span className="material-symbols-outlined text-sm">close</span>
          </button>
        </div>
      )}

      {/* Left Panel: Controls */}
      <div className="w-full h-[45%] md:h-full md:w-[400px] lg:w-[450px] flex-shrink-0 border-r border-slate-700 z-20 shadow-2xl print:hidden overflow-hidden relative order-1 md:order-1">
        <LeftPanel 
          currentStep={currentStep}
          isProcessing={isProcessing}
          onUpload={handleUpload}
          onNext={handleNext}
          onBack={handleBack}
          onDownload={handleDownload}
          onSaveDossier={handleSaveToDossier}
          onValidate={handleValidate}
          isValidated={isValidated}
          cerfaDirty={cerfaDirty}
          garageProfile={garageProfile}
          onSaveGarage={saveGarageProfile}
          isPaying={isPaying}
          activeDossierId={activeDossierId}
        />
      </div>

      {/* Right Panel: Form Preview (Interactive Mode) */}
      <div className="flex-1 h-[55%] md:h-full bg-gray-600/50 backdrop-blur-sm relative overflow-y-auto no-scrollbar print:p-0 print:bg-white print:overflow-visible print:h-auto print:block flex justify-center pt-4 md:pt-12 order-2 md:order-2">
         <div
            className="origin-top transition-transform duration-300 ease-out shadow-2xl mb-8"
            style={{ 
                transform: `scale(${previewScale})`,
                marginBottom: `${(297 * 2 * previewScale) - (297 * 2 * (1 - previewScale)) + 150}px` 
            }}
         >
            <CerfaForm 
                data={formData} 
                onChange={handleDataChange}
                isPdfMode={false} // Interactive mode
            />
         </div>

         {/* Hint text */}
         <div className="fixed bottom-4 right-4 bg-black/50 text-white px-4 py-2 rounded-full text-xs backdrop-blur-md hidden md:block print:hidden pointer-events-none z-30">
            Aperçu temps réel • {Math.round(previewScale * 100)}%
         </div>
         </div>

    </div>
      </div>
    );
  }

  // Fallback (landing) si jamais
  return (
    <div className="relative">
      <LandingPage />
    </div>
  );
};

export default App;
